import { useSDK } from '@features/app-deps-provider'
import { Organization } from '@st/sdk'
import { BrandLogoIcon, Button, CheckIcon, FullPageCard, Select } from '@st/theme'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { useProcessState } from '~/store'
import { usePromise } from '~/util/promise'
import { authModule } from '../auth/auth-module'
import { OrganizationSignUpPage } from '../organizations/pages/organization-sign-up-page'

type ConnectApp = {
  clientId: string
  name: string
  logo: string
}

const CONNECT_APPS: ConnectApp[] = [
  {
    clientId: 'karbon-production',
    name: 'Karbon',
    logo: '/icons/karbon-logo.png'
  },
  {
    clientId: 'karbon-staging',
    name: 'Karbon',
    logo: '/icons/karbon-logo.png'
  },
  {
    clientId: 'zapier',
    name: 'Zapier',
    logo: '/icons/zapier-logo.png'
  }
]

type SignUpState =
  | { status: 'idle' }
  | { status: 'signingUp' }
  | { status: 'signedUp'; organization: Organization }

type Props = {
  clientId: string
  state: string
  redirectUri: string
}
export function ConnectAppPage({ clientId, state, redirectUri }: Props) {
  const app = CONNECT_APPS.find((app) => app.clientId === clientId)!

  const authStatus = useProcessState(authModule, (s) => s.status)
  const [signUpState, setSignUpState] = useState<SignUpState>({ status: 'idle' })

  // There is an intermediate state where the user is logged in but the organization
  // has not yet been created
  //
  // We keep showing the sign up page to avoid a partially ready state
  // where are logged in as a user but they have 0 organizations (the organization is still being created)
  if (authStatus == 'loggedIn' && signUpState.status != 'signingUp') {
    return (
      <LoggedInConnectAppPage
        clientId={clientId}
        state={state}
        redirectUri={redirectUri}
        app={app}
        title={`${app.name} wants to access StanfordTax`}
      />
    )
  } else if (authStatus == 'unknown') {
    return <></>
  } else {
    return (
      <OrganizationSignUpPage
        onStartSignUp={() => setSignUpState({ status: 'signingUp' })}
        onSignedUp={(organization) => setSignUpState({ status: 'signedUp', organization })}
      />
    )
  }
}

function LoggedInConnectAppPage({
  clientId,
  state,
  redirectUri,
  app,
  title
}: {
  clientId: string
  state: string
  redirectUri: string
  app: ConnectApp
  title: string
}) {
  const sdk = useSDK()

  const membershipsResponse = usePromise(
    () => sdk.send({ type: 'organizations/getMyOrganizationMemberships' }),
    [sdk]
  )

  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>()

  const resolvedOrganizationId = match(membershipsResponse)
    .with(
      { status: 'fulfilled' },
      ({ value }) => selectedOrganizationId ?? value.memberships[0]?.organization?.id
    )
    .with({ status: 'pending' }, () => undefined)
    .with({ status: 'rejected' }, () => undefined)
    .exhaustive()

  async function onClickAuthorize() {
    setIsAuthorizing(true)
    const response = await sdk.send({
      type: 'accounts/authorizeApp',
      clientId,
      state,
      redirectUri,
      organizationId: resolvedOrganizationId!
    })
    window.location.href = response.redirectTo
    setIsAuthorizing(false)
  }

  return (
    <FullPageCard>
      <div className="flex flex-col items-center gap-3">
        <ConnectHeader app={app} title={title} />

        <Select
          className="mt-4 w-full"
          value={resolvedOrganizationId}
          placeholder={
            membershipsResponse.status == 'pending' ? 'Loading...' : 'Select an organization'
          }
          options={match(membershipsResponse)
            .with({ status: 'fulfilled' }, ({ value }) => value.memberships)
            .with({ status: 'pending' }, () => [])
            .with({ status: 'rejected' }, () => [])
            .exhaustive()}
          onChange={(orgId) => setSelectedOrganizationId(orgId)}
          buildValue={(m) => m?.organization.id}
          buildLabel={(m) => m?.organization.name}
        />

        <ul className="my-6 w-full space-y-4 px-4">
          <li className="flex gap-x-3 text-base">
            <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
            Client questionnnaire data
          </li>

          <li className="flex gap-x-3 text-base">
            <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
            Client document uploads
          </li>

          <li className="flex gap-x-3 text-base">
            <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
            Preparer workpapers
          </li>

          <li className="flex gap-x-3 text-base">
            <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
            Notifications for document uploads and questionnaire submissions
          </li>
        </ul>

        <Button
          size="m"
          className="mt-4 w-full"
          variant="primary"
          onClick={onClickAuthorize}
          disabled={isAuthorizing || !resolvedOrganizationId}
        >
          {isAuthorizing ? 'Authorizing...' : `Authorize`}
        </Button>
      </div>
    </FullPageCard>
  )
}

export function ConnectHeader({ app, title }: { app: ConnectApp; title: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-2">
        <img src={app.logo} className="size-12 rounded-md" />
        <ConnectArrow />
        <BrandLogoIcon />
      </div>
      <p className="mt-4 text-center">{title}</p>
    </div>
  )
}

function ConnectArrow() {
  return (
    <svg viewBox="0 0 24 24" className="size-6">
      <path
        fillRule="evenodd"
        d="M15.97 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06l3.22-3.22H7.5a.75.75 0 0 1 0-1.5h11.69l-3.22-3.22a.75.75 0 0 1 0-1.06Zm-7.94 9a.75.75 0 0 1 0 1.06l-3.22 3.22H16.5a.75.75 0 0 1 0 1.5H4.81l3.22 3.22a.75.75 0 1 1-1.06 1.06l-4.5-4.5a.75.75 0 0 1 0-1.06l4.5-4.5a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  )
}
